import { useState } from 'react';

import { ChevronDown, ChevronUp, Tag } from '@remarkable/ark-icons';
import { Typography } from '@remarkable/ark-web';
import { Collapse } from 'react-collapse';

import { Button, Divider, Skeleton } from 'src/components';

import { Trans, useLingui } from '@lingui/react/macro';
import type {
  CheckoutPrice,
  CheckoutPromotionPreview,
  Price,
} from 'src/api/endpoints/cloudConnect.types';
import { PriceConnect } from 'src/components/PriceConnect';
import { FreeConnectTitle, freePromotionMonths } from './FreeConnectTitle';
import { formatBillingStartDateAfterMonths } from './util';

export type AppliedPromotionSummary = {
  code: string;
  preview: CheckoutPromotionPreview;
};

// Shows the amount the customer actually pays: gross for tax-inclusive prices
// and net for tax-exclusive ones, matching the regular summary.
const toDisplayPrice = (price: CheckoutPrice, period?: Price['period']) => {
  const amount = price.taxIncluded
    ? price.totalAmount
    : price.totalAmountExcludingTax;
  return {
    currency: price.currency,
    precision: price.precision,
    quantity: 1,
    totalAmount: amount,
    unitAmount: amount,
    period,
  } satisfies Price;
};

export function PromotionSummary({
  promotion,
  isLoadingPrice,
}: {
  promotion: AppliedPromotionSummary;
  isLoadingPrice: boolean;
}) {
  const { t, i18n } = useLingui();
  const [showDetails, setShowDetails] = useState(false);
  const { preview } = promotion;
  const months = freePromotionMonths(preview.appliedPromotion);

  const title =
    months === undefined ? (
      t`Connect subscription`
    ) : (
      <span className="inline-flex items-center gap-8">
        <FreeConnectTitle months={months} />
        <Tag
          size={20}
          aria-hidden
          className="text-pen-blue"
          data-cy="promotion-title-icon"
        />
      </span>
    );

  const initialPrice = toDisplayPrice(preview.price);
  const regularPrice = toDisplayPrice(preview.regularPrice);
  const billingStartDate =
    months === undefined
      ? ''
      : formatBillingStartDateAfterMonths(months, i18n.locale);
  const billingStartsText = t`Billing starts ${billingStartDate}`;
  const percentOff = preview.appliedPromotion.percentOff;
  const discountText = t`${percentOff}% off`;
  const reminderText = t`We will send you a reminder before your first payment is due.`;
  const promoCodeLabel = t`Promo code`;

  const rows =
    months === undefined ? (
      <div className="flex justify-between" data-cy="promotion-discount-row">
        <p className="my-4">{discountText}</p>
      </div>
    ) : (
      <div className="flex justify-between" data-cy="billing-starts-row">
        <p data-cy="billing-start-date" className="my-4">
          {billingStartsText}
        </p>
        <p className="my-4">
          <Skeleton isLoading={isLoadingPrice}>
            {preview.regularPrice.taxIncluded ? (
              <Trans>
                <PriceConnect price={regularPrice} hidePeriodLabel />
                /mo. (incl. tax)
              </Trans>
            ) : (
              <Trans>
                <PriceConnect price={regularPrice} hidePeriodLabel />
                /mo. (excl. tax)
              </Trans>
            )}
          </Skeleton>
        </p>
      </div>
    );

  const details = (
    <>
      {rows}
      <div className="flex justify-between" data-cy="promotion-code-row">
        <p className="my-4">{promoCodeLabel}</p>
        <p className="my-4" data-cy="promotion-code-used">
          {promotion.code}
        </p>
      </div>
      {months !== undefined && (
        <>
          <Divider className="my-12" />
          <Typography variant="body-sm" data-cy="promotion-reminder">
            {reminderText}
          </Typography>
        </>
      )}
    </>
  );

  return (
    <div data-cy="promotion-summary">
      <div className="flex w-full flex-col bg-neutral-light-3 p-8 ll:hidden">
        <Button
          variant="tertiary-neutral"
          className="w-full bg-none"
          onClick={() => setShowDetails((showDetails) => !showDetails)}
          data-cy="order-summary-toggle"
          aria-expanded={showDetails}
        >
          <span className="flex w-full items-center justify-between">
            <span className="mr-auto">{title}</span>
            <span>
              <Skeleton isLoading={isLoadingPrice}>
                <b data-cy="connect-plan-row-price">
                  <PriceConnect price={initialPrice} hidePeriodLabel />
                </b>
              </Skeleton>
            </span>
            {showDetails ? (
              <ChevronUp className="ml-16 inline max-w-20" />
            ) : (
              <ChevronDown className="ml-16 inline max-w-20" />
            )}
          </span>
        </Button>
        <Collapse isOpened={showDetails}>
          <div
            className="flex flex-col bg-neutral-light-3 px-16 pb-16"
            data-cy="order-summary-details"
          >
            <Divider className="my-12" />
            {details}
          </div>
        </Collapse>
      </div>

      <div className="hidden flex-col bg-neutral-light-3 p-20 ll:flex">
        <div data-cy="connect-plan-row" className="flex justify-between">
          <Typography variant="body-md" className="my-4 font-semibold">
            {title}
          </Typography>
          <p className="my-4">
            <b>
              <Skeleton isLoading={isLoadingPrice}>
                <PriceConnect price={initialPrice} hidePeriodLabel />
              </Skeleton>
            </b>
          </p>
        </div>
        <Divider className="my-12" />
        {details}
      </div>
    </div>
  );
}
